import * as React from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Send, LifeBuoy, RefreshCw } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Field } from '@/components/ui/field';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/sonner';
import { env } from '@/lib/env';
import { cn } from '@/lib/cn';

/**
 * Clinic support thread — symmetric to the operator's /inbox view.
 *
 * The clinic posts a message; the operator sees it in their CF mailbox
 * inbox alongside everything else. Operator replies via the existing
 * /api/operator/conversations/:id/reply, which fires an email via the
 * OUTBOUND_EMAIL binding and inserts an outbound message row. Clinic
 * sees the reply here on the next refetch.
 *
 * No mailto: link — everything goes through the API so we have an
 * auditable thread inside the product, not a black-hole email chain.
 */
export const Route = createFileRoute('/_app/help')({
  staticData: { crumb: 'Pomoc' },
  beforeLoad: ({ context }) => {
    const ctx = context as { session: { role: string } };
    if (ctx.session.role === 'operator') throw redirect({ to: '/inbox' });
  },
  component: HelpPage,
});

interface SupportMessage {
  id: string;
  direction: 'inbound' | 'outbound';
  from_addr: string;
  subject: string;
  body_text: string | null;
  ts: number;
}
interface SupportThread {
  conversation: { id: string; last_message_at: number | null; created_at: number } | null;
  messages: SupportMessage[];
}

function HelpPage() {
  const qc = useQueryClient();
  const thread = useQuery({
    queryKey: ['support', 'thread'],
    queryFn: async (): Promise<SupportThread> => {
      const res = await fetch(`${env.apiBaseUrl}/api/admin/support`, { credentials: 'include' });
      if (!res.ok) throw new Error('load_failed');
      return res.json();
    },
    refetchInterval: 30_000,
  });

  const send = useMutation({
    mutationFn: async (input: { subject?: string; body_text: string }) => {
      const res = await fetch(`${env.apiBaseUrl}/api/admin/support/messages`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? 'send_failed');
      }
      return res.json();
    },
    onSuccess: () => {
      setSubject('');
      setBody('');
      qc.invalidateQueries({ queryKey: ['support'] });
      toast.success('Wiadomość wysłana — odpowiemy do końca dnia roboczego');
    },
    onError: () => {
      toast.error(
        'Nie udało się wysłać. Spróbuj ponownie lub napisz na max@pozyskajpacjenta.pl.'
      );
    },
  });

  const [subject, setSubject] = React.useState('');
  const [body, setBody] = React.useState('');

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header>
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 font-display text-2xl font-semibold tracking-tight text-text">
              <LifeBuoy className="size-5 text-accent" aria-hidden="true" />
              Pomoc / kontakt z zespołem
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-text-muted">
              Napisz, czego potrzebujesz — zmiany copywritingu, problem techniczny, pytanie o
              fakturę, prośba o customową integrację. Odpisujemy zwykle w ciągu jednego dnia
              roboczego.
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => qc.invalidateQueries({ queryKey: ['support'] })}
            disabled={thread.isFetching}
          >
            <RefreshCw className={cn('size-3.5', thread.isFetching && 'animate-spin')} aria-hidden="true" />
            Odśwież
          </Button>
        </div>
      </header>

      {/* Thread */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Wątek</CardTitle>
          <CardDescription>
            Historia wszystkich Twoich wiadomości i odpowiedzi zespołu. Operator widzi to samo w
            swojej skrzynce <code className="rounded bg-surface px-1 py-0.5 text-xs">/inbox</code>.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {thread.isPending ? (
            <div className="space-y-3">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : (thread.data?.messages ?? []).length === 0 ? (
            <p className="rounded-md bg-surface p-4 text-sm text-text-muted ring-1 ring-black/5">
              Brak wiadomości. Napisz pierwszą poniżej — nasz zespół widzi ją od razu.
            </p>
          ) : (
            <ul role="list" className="space-y-3">
              {thread.data!.messages.map((m) => {
                const time = new Date(m.ts * 1000).toLocaleString('pl-PL', {
                  timeZone: 'Europe/Warsaw',
                  dateStyle: 'medium',
                  timeStyle: 'short',
                });
                const isMine = m.direction === 'inbound';
                return (
                  <li key={m.id}>
                    <div
                      className={cn(
                        'rounded-md p-4 ring-1',
                        isMine
                          ? 'bg-surface ring-black/5'
                          : 'bg-accent/5 ring-accent/20'
                      )}
                    >
                      <div className="flex flex-wrap items-baseline justify-between gap-2 text-xs">
                        <p className="font-medium text-text">
                          {isMine ? 'Ty' : 'Zespół pozyskajpacjenta.pl'}
                          <span className="ml-2 font-mono text-[10px] text-text-subtle">
                            {m.from_addr}
                          </span>
                        </p>
                        <p className="text-text-subtle">{time}</p>
                      </div>
                      {m.subject ? (
                        <p className="mt-1 text-xs text-text-muted">{m.subject}</p>
                      ) : null}
                      {m.body_text ? (
                        <pre className="mt-2 whitespace-pre-wrap break-words font-sans text-sm leading-relaxed text-text">
                          {m.body_text}
                        </pre>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Composer */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Napisz wiadomość</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Field label="Temat (opcjonalnie)" htmlFor="subj" hint='np. „prośba o zmianę zdjęcia w hero"'>
            <Input id="subj" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </Field>
          <Field label="Treść" htmlFor="body" hint="Min. 2 znaki, max. 8000.">
            <Textarea
              id="body"
              rows={6}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Cześć,&#10;&#10;…"
            />
          </Field>
          <div className="flex justify-end">
            <Button
              type="button"
              disabled={body.trim().length < 2 || send.isPending}
              onClick={() =>
                send.mutate({
                  subject: subject.trim() || undefined,
                  body_text: body.trim(),
                })
              }
            >
              <Send className="size-3.5" aria-hidden="true" />
              {send.isPending ? 'Wysyłam…' : 'Wyślij'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
